import OpenStudioLogoOnLight from "@/assets/images/openstudio-logo-on-light.svg";
import { AUTH_COLORS } from "@/components/auth/authScreenStyles";
import { Ionicons } from "@expo/vector-icons";
import { useFocusEffect } from "expo-router";
import React, { useCallback, useState } from "react";
import {
  KeyboardAvoidingView,
  LayoutChangeEvent,
  Platform,
  Pressable,
  ScrollView,
  StatusBar,
  StyleSheet,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type Props = {
  children: React.ReactNode;
  onBack?: () => void;
};

export function AuthScreenShell({ children, onBack }: Props) {
  const insets = useSafeAreaInsets();
  const isWeb = Platform.OS === "web";
  const topInset = Math.max(insets.top, 12);
  const [viewportH, setViewportH] = useState(0);
  const [contentH, setContentH] = useState(0);
  // Center short forms (login); long forms (signup) start at top so scroll reaches header/footer.
  const centerContent =
    viewportH > 0 && contentH > 0 && contentH < viewportH - 24;

  useFocusEffect(
    useCallback(() => {
      StatusBar.setBarStyle("dark-content", true);
      if (Platform.OS === "android") {
        StatusBar.setBackgroundColor(AUTH_COLORS.pageBg, true);
      }
      return () => {
        StatusBar.setBarStyle("light-content", true);
        if (Platform.OS === "android") {
          StatusBar.setBackgroundColor("#fff", true);
        }
      };
    }, []),
  );

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
    >
      <StatusBar
        barStyle="dark-content"
        backgroundColor={AUTH_COLORS.pageBg}
      />

      {onBack ? (
        <View
          style={[
            styles.headerRow,
            {
              paddingTop: topInset,
              paddingLeft: Math.max(insets.left, 12),
            },
          ]}
        >
          <Pressable
            onPress={onBack}
            hitSlop={12}
            style={styles.backButton}
            accessibilityLabel="Go back"
            accessibilityRole="button"
          >
            <Ionicons name="chevron-back" size={24} color={AUTH_COLORS.text} />
          </Pressable>
        </View>
      ) : null}

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          centerContent ? styles.scrollCenter : styles.scrollTop,
          {
            // The back row already clears the notch; without it the content
            // has to hold that space itself.
            paddingTop: onBack ? 16 : topInset + (isWeb ? 24 : 12),
            paddingBottom: Math.max(insets.bottom, 16) + 32,
          },
        ]}
        onLayout={(e: LayoutChangeEvent) =>
          setViewportH(e.nativeEvent.layout.height)
        }
        showsVerticalScrollIndicator
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
      >
        <View
          style={styles.inner}
          onLayout={(e: LayoutChangeEvent) =>
            setContentH(e.nativeEvent.layout.height)
          }
        >
          <View style={styles.logoWrap}>
            <OpenStudioLogoOnLight
              width={isWeb ? 168 : 148}
              height={isWeb ? 28 : 25}
              accessibilityLabel="Open Studio"
            />
          </View>
          {children}
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: AUTH_COLORS.pageBg,
  },
  headerRow: {
    width: "100%",
    paddingBottom: 4,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: AUTH_COLORS.cardBg,
    alignItems: "center",
    justifyContent: "center",
    ...(Platform.OS === "web"
      ? {
          boxShadow: "0 1px 3px rgba(13, 13, 13, 0.10)",
        }
      : {
          shadowColor: "#0D0D0D",
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.1,
          shadowRadius: 3,
          elevation: 2,
        }),
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: "center",
    paddingHorizontal: 20,
    width: "100%",
  },
  scrollCenter: {
    justifyContent: "center",
  },
  scrollTop: {
    justifyContent: "flex-start",
  },
  inner: {
    width: "100%",
    maxWidth: 440,
    alignItems: "center",
  },
  logoWrap: {
    width: "100%",
    alignItems: "center",
    marginBottom: 24,
  },
});
